// Quiz.tsx
import React, { useMemo, useState, useCallback } from "react";
import QuestionView from "./QuestionView";
import { useMyDialog } from "@/context/MyDialogProvider";
import { QuizResult, Question } from "./types";
import { QuizHeader } from "./QuizHeader";
import { QuizResults } from "./QuizResults";

interface QuizProps {
  title: string;
  description?: string;
  studentName?: string;
  passingPercentage?: number;
  questions: Question[];
  primaryColor?: string;
  showExplanation?: boolean;
  onComplete?: (result: QuizResult) => void;
}

export default function Quiz({
  title,
  description,
  studentName = "Student",
  passingPercentage = 60,
  questions,
  primaryColor = "bg-black",
  showExplanation = true,
  onComplete,
}: QuizProps) {
  const { open, closeAll } = useMyDialog(); // Added closeAll from the hook
  
  const [quizState, setQuizState] = useState({
    currentQuestion: 0,
    selected: null as string | null,
    score: 0,
    showAnswer: false,
    finished: false,
  });

  const currentQuestion = useMemo(
    () => questions[quizState.currentQuestion],
    [quizState.currentQuestion, questions]
  );

  const percentage = useMemo(
    () => Math.round((quizState.score / questions.length) * 100),
    [quizState.score, questions.length]
  );

  const passed = percentage >= passingPercentage;

  const handleSelect = useCallback((option: string) => {
    if (quizState.showAnswer) return;

    setQuizState(prev => ({
      ...prev,
      selected: option,
      showAnswer: true,
      score: prev.score + (option === currentQuestion.answer ? 1 : 0),
    }));
  }, [quizState.showAnswer, currentQuestion.answer]);

  const handleNext = useCallback(() => {
    const isLastQuestion = quizState.currentQuestion + 1 >= questions.length;
    
    if (isLastQuestion) {
      const result = { 
        score: quizState.score, 
        total: questions.length, 
        percentage, 
        passed 
      };
      setQuizState(prev => ({ ...prev, finished: true }));
      onComplete?.(result);
    } else {
      setQuizState(prev => ({
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
        selected: null,
        showAnswer: false,
      }));
    }
  }, [quizState.currentQuestion, quizState.score, questions.length, percentage, passed, onComplete]);

  const restartQuiz = useCallback(() => {
    setQuizState({
      currentQuestion: 0,
      selected: null,
      score: 0,
      showAnswer: false,
      finished: false,
    });
  }, []);

  const handleViewQuestion = useCallback((question: Question) => {
    open({
      title: `Question ${question.id}`,
      size: "lg",
      content: (
        <QuestionView
          question={question}
          currentQuestion={0}
          totalQuestions={1}
          selected={null}
          showAnswer={true}
          score={0}
          primaryColor={primaryColor}
          onSelect={() => {}}
          onNext={() => closeAll()} // Now closeAll is defined
          isInDialog={true}
        />
      ),
      onClose: () => {},
    });
  }, [open, closeAll, primaryColor]);

  // If quiz is finished, show results
  if (quizState.finished) {
    return (
      <QuizResults
        title={title}
        studentName={studentName}
        score={quizState.score}
        total={questions.length}
        percentage={percentage}
        passed={passed}
        primaryColor={primaryColor}
        onRestart={restartQuiz}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <div className="bg-white shadow-xl rounded-3xl border overflow-hidden">
        <QuizHeader
          title={title}
          description={description}
          currentQuestion={quizState.currentQuestion}
          totalQuestions={questions.length}
          primaryColor={primaryColor}
        />
        
        <QuestionView
          question={currentQuestion}
          currentQuestion={quizState.currentQuestion}
          totalQuestions={questions.length}
          selected={quizState.selected}
          showAnswer={quizState.showAnswer}
          score={quizState.score}
          primaryColor={primaryColor}
          onSelect={handleSelect}
          onNext={handleNext}
          onViewQuestion={handleViewQuestion}
        />
      </div>
    </div>
  );
}